import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import type { FavoriteTeam, FavoritePlayer } from "@/hooks/useUserFavorites";

interface DbUserTeam {
  id: string;
  user_id: string;
  team_id: string;
  team_name: string;
  team_abbr: string;
  league: string;
  logo_url: string | null;
  is_favorite: boolean;
  added_at: string;
}

interface DbUserPlayer {
  id: string;
  user_id: string;
  player_id: number;
  player_name: string;
  team: string;
  team_logo: string | null;
  league: string;
  position: string;
  number: number | null;
  photo: string | null;
  nationality: string | null;
  is_favorite: boolean;
  added_at: string;
}

export function useUserFavoritesSync() {
  const { user } = useAuth();
  const [dbTeams, setDbTeams] = useState<FavoriteTeam[]>([]);
  const [dbPlayers, setDbPlayers] = useState<FavoritePlayer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch teams from database
  const fetchTeams = useCallback(async () => {
    if (!user) {
      setDbTeams([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("user_teams")
        .select("*")
        .eq("user_id", user.id)
        .order("added_at", { ascending: false });

      if (error) throw error;

      const teams: FavoriteTeam[] = (data as DbUserTeam[]).map((t) => ({
        id: t.team_id,
        name: t.team_name,
        abbr: t.team_abbr,
        league: t.league,
        logo: t.logo_url || undefined,
      }));

      setDbTeams(teams);
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
  }, [user]);

  // Fetch players from database
  const fetchPlayers = useCallback(async () => {
    if (!user) {
      setDbPlayers([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("user_players")
        .select("*")
        .eq("user_id", user.id)
        .order("added_at", { ascending: false });

      if (error) throw error;

      const players: FavoritePlayer[] = (data as DbUserPlayer[]).map((p) => ({
        id: p.player_id,
        name: p.player_name,
        team: p.team,
        teamLogo: p.team_logo || undefined,
        league: p.league,
        position: p.position,
        number: p.number || undefined,
        photo: p.photo || undefined,
        nationality: p.nationality || undefined,
      }));

      setDbPlayers(players);
    } catch (error) {
      console.error("Error fetching players:", error);
    }
  }, [user]);

  // Initial fetch
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchTeams(), fetchPlayers()]);
      setIsLoading(false);
    };

    loadData();
  }, [fetchTeams, fetchPlayers]);

  // Save teams to database
  const saveTeams = useCallback(
    async (teams: FavoriteTeam[]) => {
      if (!user) {
        toast.error("You must be logged in to save teams");
        return false;
      }

      setIsSaving(true);

      try {
        // Delete all existing teams for user
        const { error: deleteError } = await supabase
          .from("user_teams")
          .delete()
          .eq("user_id", user.id);

        if (deleteError) throw deleteError;

        // Insert new teams
        if (teams.length > 0) {
          const teamsToInsert = teams.map((t) => ({
            user_id: user.id,
            team_id: t.id,
            team_name: t.name,
            team_abbr: t.abbr,
            league: t.league,
            logo_url: t.logo || null,
            is_favorite: true,
          }));

          const { error: insertError } = await supabase
            .from("user_teams")
            .insert(teamsToInsert);

          if (insertError) throw insertError;
        }

        setDbTeams(teams);
        toast.success("Teams saved successfully!");
        return true;
      } catch (error) {
        console.error("Error saving teams:", error);
        toast.error("Failed to save teams");
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [user]
  );

  // Save players to database
  const savePlayers = useCallback(
    async (players: FavoritePlayer[]) => {
      if (!user) {
        toast.error("You must be logged in to save players");
        return false;
      }

      setIsSaving(true);

      try {
        // Delete all existing players for user
        const { error: deleteError } = await supabase
          .from("user_players")
          .delete()
          .eq("user_id", user.id);

        if (deleteError) throw deleteError;

        // Insert new players
        if (players.length > 0) {
          const playersToInsert = players.map((p) => ({
            user_id: user.id,
            player_id: p.id,
            player_name: p.name,
            team: p.team,
            team_logo: p.teamLogo || null,
            league: p.league,
            position: p.position,
            number: p.number || null,
            photo: p.photo || null,
            nationality: p.nationality || null,
            is_favorite: true,
          }));

          const { error: insertError } = await supabase
            .from("user_players")
            .insert(playersToInsert);

          if (insertError) throw insertError;
        }

        setDbPlayers(players);
        toast.success("Players saved successfully!");
        return true;
      } catch (error) {
        console.error("Error saving players:", error);
        toast.error("Failed to save players");
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [user]
  );

  // Add single team
  const addTeam = useCallback(
    async (team: FavoriteTeam) => {
      if (!user) return false;

      try {
        const { error } = await supabase.from("user_teams").insert({
          user_id: user.id,
          team_id: team.id,
          team_name: team.name,
          team_abbr: team.abbr,
          league: team.league,
          logo_url: team.logo || null,
          is_favorite: true,
        });

        if (error) throw error;

        setDbTeams((prev) => [...prev, team]);
        return true;
      } catch (error) {
        console.error("Error adding team:", error);
        return false;
      }
    },
    [user]
  );

  // Remove single team
  const removeTeam = useCallback(
    async (teamId: string, league: string) => {
      if (!user) return false;

      try {
        const { error } = await supabase
          .from("user_teams")
          .delete()
          .eq("user_id", user.id)
          .eq("team_id", teamId)
          .eq("league", league);

        if (error) throw error;

        setDbTeams((prev) =>
          prev.filter((t) => !(t.id === teamId && t.league === league))
        );
        return true;
      } catch (error) {
        console.error("Error removing team:", error);
        return false;
      }
    },
    [user]
  );

  // Add single player
  const addPlayer = useCallback(
    async (player: FavoritePlayer) => {
      if (!user) return false;

      try {
        const { error } = await supabase.from("user_players").insert({
          user_id: user.id,
          player_id: player.id,
          player_name: player.name,
          team: player.team,
          team_logo: player.teamLogo || null,
          league: player.league,
          position: player.position,
          number: player.number || null,
          photo: player.photo || null,
          nationality: player.nationality || null,
          is_favorite: true,
        });

        if (error) throw error;

        setDbPlayers((prev) => [...prev, player]);
        return true;
      } catch (error) {
        console.error("Error adding player:", error);
        return false;
      }
    },
    [user]
  );

  // Remove single player
  const removePlayer = useCallback(
    async (playerId: number, league: string) => {
      if (!user) return false;

      try {
        const { error } = await supabase
          .from("user_players")
          .delete()
          .eq("user_id", user.id)
          .eq("player_id", playerId)
          .eq("league", league);

        if (error) throw error;

        setDbPlayers((prev) =>
          prev.filter((p) => !(p.id === playerId && p.league === league))
        );
        return true;
      } catch (error) {
        console.error("Error removing player:", error);
        return false;
      }
    },
    [user]
  );

  return {
    dbTeams,
    dbPlayers,
    isLoading,
    isSaving,
    saveTeams,
    savePlayers,
    addTeam,
    removeTeam,
    addPlayer,
    removePlayer,
    refetch: () => Promise.all([fetchTeams(), fetchPlayers()]),
  };
}
